import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  FileText,
  GripVertical,
  AlertTriangle,
  Loader2,
  Shield,
  CreditCard,
  Receipt,
  FolderCheck,
  File,
  Download,
  Check,
  Search,
} from 'lucide-react';
import { intakeService } from '@/services/api/intake';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { IntakeDocumentType, DocumentCategory, CreateDocumentTypeInput, UpdateDocumentTypeInput, ExtractionField, ExtractionFieldType } from '@/types/intake';

const CATEGORY_ICONS: Record<DocumentCategory, React.ElementType> = {
  identification: Shield,
  personal_information: Shield,
  financial: CreditCard,
  income: CreditCard,
  tax: Receipt,
  deductions: Receipt,
  compliance: FolderCheck,
  superannuation: FolderCheck,
  offsets_rebates: FolderCheck,
  other: File,
};

const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  personal_information: 'Personal Information',
  identification: 'Identification',
  income: 'Income',
  financial: 'Financial',
  deductions: 'Deductions',
  tax: 'Tax',
  superannuation: 'Superannuation',
  offsets_rebates: 'Offsets & Rebates',
  compliance: 'Compliance',
  other: 'Other',
};

const FIELD_TYPES: { value: ExtractionFieldType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'integer', label: 'Integer' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'enum', label: 'Dropdown' },
];

export function IntakeDocumentTypesPage() {
  const queryClient = useQueryClient();

  // State
  const [editingDocType, setEditingDocType] = useState<IntakeDocumentType | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docTypeToDelete, setDocTypeToDelete] = useState<IntakeDocumentType | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importSearchTerm, setImportSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState<CreateDocumentTypeInput & { extractionFields?: ExtractionField[] }>({
    name: '',
    description: '',
    category: 'other',
    isRequired: true,
    acceptedMimeTypes: ['application/pdf'],
    maxFileSizeMb: 20,
    extractionFields: [],
  });

  // Extraction fields editor state
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [fieldFormData, setFieldFormData] = useState<ExtractionField>({
    key: '',
    label: '',
    type: 'string',
    required: false,
  });

  // Fetch document types
  const { data: documentTypesData, isLoading } = useQuery({
    queryKey: ['intake-document-types'],
    queryFn: () => intakeService.listDocumentTypes(),
  });

  const documentTypes = documentTypesData?.documentTypes || [];

  // Fetch system document types for import (only when dialog is open)
  const { data: systemDocTypesData, isLoading: isLoadingSystemTypes } = useQuery({
    queryKey: ['system-document-types'],
    queryFn: () => intakeService.listSystemDocumentTypes({ isActive: true }),
    enabled: importDialogOpen,
  });

  const systemDocumentTypes = systemDocTypesData?.documentTypes || [];

  // Group by category
  const groupedDocTypes = documentTypes.reduce((acc, docType) => {
    const category = docType.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(docType);
    return acc;
  }, {} as Record<DocumentCategory, IntakeDocumentType[]>);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (input: CreateDocumentTypeInput) => intakeService.createDocumentType(input),
    onSuccess: () => {
      toast.success('Document type created successfully');
      queryClient.invalidateQueries({ queryKey: ['intake-document-types'] });
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: () => {
      toast.error('Failed to create document type');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDocumentTypeInput }) =>
      intakeService.updateDocumentType(id, input),
    onSuccess: () => {
      toast.success('Document type updated successfully');
      queryClient.invalidateQueries({ queryKey: ['intake-document-types'] });
      setEditingDocType(null);
      resetForm();
    },
    onError: () => {
      toast.error('Failed to update document type');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => intakeService.deleteDocumentType(id),
    onSuccess: () => {
      toast.success('Document type deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['intake-document-types'] });
      setDeleteDialogOpen(false);
      setDocTypeToDelete(null);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Failed to delete document type';
      setDeleteDialogOpen(false);
      if (errorMessage.includes('documents are using it')) {
        const docId = docTypeToDelete?.id;
        toast.error('Cannot delete: Documents are using this type.', {
          duration: 10000,
          action: {
            label: 'Deactivate Instead',
            onClick: () => {
              if (docId) {
                toggleActiveMutation.mutate({ id: docId, isActive: false });
              }
            },
          },
        });
      } else {
        toast.error(errorMessage);
      }
      setDocTypeToDelete(null);
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      intakeService.updateDocumentType(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-document-types'] });
      toast.success('Document type updated');
    },
    onError: () => {
      toast.error('Failed to update document type');
    },
  });

  // Import from system mutation
  const importMutation = useMutation({
    mutationFn: (systemDocTypeId: string) => intakeService.copySystemDocumentTypeToTenant(systemDocTypeId),
    onSuccess: (data) => {
      toast.success(`Imported "${data.name}" successfully`);
      queryClient.invalidateQueries({ queryKey: ['intake-document-types'] });
    },
    onError: () => {
      toast.error('Failed to import document type');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'other',
      isRequired: true,
      acceptedMimeTypes: ['application/pdf'],
      maxFileSizeMb: 20,
      extractionFields: [],
    });
    setEditingFieldIndex(null);
    setFieldFormData({ key: '', label: '', type: 'string', required: false });
  };

  const openEditDialog = (docType: IntakeDocumentType) => {
    setEditingDocType(docType);
    setFormData({
      name: docType.name,
      description: docType.description || '',
      category: docType.category,
      isRequired: docType.isRequired,
      acceptedMimeTypes: docType.acceptedMimeTypes,
      maxFileSizeMb: docType.maxFileSizeMb,
      extractionFields: docType.extractionFields || [],
    });
  };

  // Extraction field management
  const addExtractionField = () => {
    if (!fieldFormData.key || !fieldFormData.label) {
      toast.error('Field key and label are required');
      return;
    }
    const currentFields = formData.extractionFields || [];
    if (currentFields.some(f => f.key === fieldFormData.key)) {
      toast.error('A field with this key already exists');
      return;
    }
    setFormData({
      ...formData,
      extractionFields: [...currentFields, { ...fieldFormData }],
    });
    setFieldFormData({ key: '', label: '', type: 'string', required: false });
  };

  const updateExtractionField = (index: number) => {
    if (!fieldFormData.key || !fieldFormData.label) {
      toast.error('Field key and label are required');
      return;
    }
    const currentFields = formData.extractionFields || [];
    const updated = [...currentFields];
    updated[index] = { ...fieldFormData };
    setFormData({ ...formData, extractionFields: updated });
    setEditingFieldIndex(null);
    setFieldFormData({ key: '', label: '', type: 'string', required: false });
  };

  const removeExtractionField = (index: number) => {
    const currentFields = formData.extractionFields || [];
    setFormData({
      ...formData,
      extractionFields: currentFields.filter((_, i) => i !== index),
    });
  };

  const startEditingField = (index: number) => {
    const currentFields = formData.extractionFields || [];
    setEditingFieldIndex(index);
    setFieldFormData({ ...currentFields[index] });
  };

  const handleSave = () => {
    if (editingDocType) {
      updateMutation.mutate({ id: editingDocType.id, input: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getCategoryIcon = (category: DocumentCategory) => {
    const Icon = CATEGORY_ICONS[category] || FileText;
    return Icon;
  };

  // Check if a system document type is already imported
  const isAlreadyImported = (systemDocTypeId: string) => {
    return documentTypes.some(dt => dt.systemDocumentTypeId === systemDocTypeId);
  };

  // Filter and group system document types for import
  const filteredSystemDocTypes = systemDocumentTypes.filter((sdt: any) => {
    if (!importSearchTerm) return true;
    const searchLower = importSearchTerm.toLowerCase();
    return (
      sdt.name?.toLowerCase().includes(searchLower) ||
      sdt.description?.toLowerCase().includes(searchLower) ||
      sdt.category?.toLowerCase().includes(searchLower)
    );
  });

  const groupedSystemDocTypes = filteredSystemDocTypes.reduce((acc: Record<string, any[]>, docType: any) => {
    const category = docType.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(docType);
    return acc;
  }, {} as Record<string, any[]>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Document Types</h1>
          <p className="text-muted-foreground">
            Define the documents clients need to upload for intake
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <Download className="mr-2 h-4 w-4" />
            Import from System
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Document Type
          </Button>
        </div>
      </div>

      {/* Document Types by Category */}
      {isLoading ? (
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2].map((j) => (
                    <Skeleton key={j} className="h-20 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : documentTypes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">No document types defined</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create document types that clients will need to upload for intake
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add First Document Type
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {(Object.keys(CATEGORY_LABELS) as DocumentCategory[]).map((category) => {
            const docTypes = groupedDocTypes[category];
            if (!docTypes || docTypes.length === 0) return null;

            const CategoryIcon = getCategoryIcon(category);

            return (
              <Card key={category}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <CategoryIcon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-lg">{CATEGORY_LABELS[category]}</CardTitle>
                    <Badge variant="secondary">{docTypes.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {docTypes
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((docType) => (
                        <div
                          key={docType.id}
                          className={cn(
                            'flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors',
                            !docType.isActive && 'opacity-50'
                          )}
                        >
                          <div className="text-muted-foreground cursor-move">
                            <GripVertical className="h-5 w-5" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{docType.name}</p>
                              {docType.isRequired && (
                                <Badge variant="destructive" className="text-xs">Required</Badge>
                              )}
                              {!docType.isActive && (
                                <Badge variant="secondary" className="text-xs">Inactive</Badge>
                              )}
                            </div>
                            {docType.description && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                                {docType.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>Max: {docType.maxFileSizeMb}MB</span>
                              <span>
                                Formats: {docType.acceptedMimeTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')}
                              </span>
                              {docType.extractionFields && docType.extractionFields.length > 0 && (
                                <span>{docType.extractionFields.length} extraction fields</span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`active-${docType.id}`} className="text-xs text-muted-foreground">
                                Active
                              </Label>
                              <Switch
                                id={`active-${docType.id}`}
                                checked={docType.isActive}
                                onCheckedChange={(checked) =>
                                  toggleActiveMutation.mutate({ id: docType.id, isActive: checked })
                                }
                              />
                            </div>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(docType)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => {
                                    setDocTypeToDelete(docType);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || !!editingDocType}
        onOpenChange={(open) => {
          if (!open) {
            setCreateDialogOpen(false);
            setEditingDocType(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDocType ? 'Edit Document Type' : 'Add Document Type'}
            </DialogTitle>
            <DialogDescription>
              {editingDocType
                ? 'Update the document type settings'
                : 'Define a new document type for client intake'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Photo ID, Bank Statement"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this document should contain..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v as DocumentCategory })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(CATEGORY_LABELS) as DocumentCategory[]).map((cat) => (
                      <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxSize">Max File Size (MB)</Label>
                <Input
                  id="maxSize"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.maxFileSizeMb}
                  onChange={(e) => setFormData({ ...formData, maxFileSizeMb: parseInt(e.target.value) || 20 })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="required">Required Document</Label>
                <p className="text-xs text-muted-foreground">
                  Clients must upload this to complete intake
                </p>
              </div>
              <Switch
                id="required"
                checked={formData.isRequired}
                onCheckedChange={(checked) => setFormData({ ...formData, isRequired: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label>Accepted File Formats</Label>
              <div className="flex flex-wrap gap-2">
                {['application/pdf', 'image/jpeg', 'image/png'].map((mime) => {
                  const isSelected = formData.acceptedMimeTypes?.includes(mime);
                  const label = mime.split('/')[1].toUpperCase();
                  return (
                    <Badge
                      key={mime}
                      variant={isSelected ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        const current = formData.acceptedMimeTypes || [];
                        const updated = isSelected
                          ? current.filter(m => m !== mime)
                          : [...current, mime];
                        setFormData({ ...formData, acceptedMimeTypes: updated });
                      }}
                    >
                      {label}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Extraction Fields Section */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Expected Extraction Fields</Label>
                <Badge variant="secondary">{formData.extractionFields?.length || 0} fields</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Define the data fields you expect to extract from this document type
              </p>

              {/* Current Fields List */}
              {formData.extractionFields && formData.extractionFields.length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                  {formData.extractionFields.map((field, index) => (
                    <div
                      key={field.key}
                      className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{field.label}</span>
                          {field.required && (
                            <Badge variant="destructive" className="text-xs">Required</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {field.key} ({field.type}){field.format && ` - ${field.format}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => startEditingField(index)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => removeExtractionField(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Field Form */}
              <div className="border rounded-md p-3 space-y-3 bg-muted/30">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Field Key *</Label>
                    <Input
                      placeholder="e.g., firstName"
                      value={fieldFormData.key}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, key: e.target.value.replace(/\s/g, '') })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Display Label *</Label>
                    <Input
                      placeholder="e.g., First Name"
                      value={fieldFormData.label}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, label: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select
                      value={fieldFormData.type}
                      onValueChange={(v) => setFieldFormData({ ...fieldFormData, type: v as ExtractionFieldType })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((ft) => (
                          <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Format</Label>
                    <Input
                      placeholder="e.g., DD/MM/YYYY"
                      value={fieldFormData.format || ''}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, format: e.target.value || undefined })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex items-center gap-1">
                      <Switch
                        id="field-required"
                        checked={fieldFormData.required}
                        onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })}
                      />
                      <Label htmlFor="field-required" className="text-xs">Required</Label>
                    </div>
                  </div>
                </div>
                {fieldFormData.type === 'enum' && (
                  <div>
                    <Label className="text-xs">Options (comma-separated)</Label>
                    <Input
                      placeholder="Option 1, Option 2, Option 3"
                      value={fieldFormData.options?.join(', ') || ''}
                      onChange={(e) => setFieldFormData({
                        ...fieldFormData,
                        options: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                      })}
                      className="h-8 text-sm"
                    />
                  </div>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => editingFieldIndex !== null ? updateExtractionField(editingFieldIndex) : addExtractionField()}
                  disabled={!fieldFormData.key || !fieldFormData.label}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {editingFieldIndex !== null ? 'Update Field' : 'Add Field'}
                </Button>
                {editingFieldIndex !== null && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingFieldIndex(null);
                      setFieldFormData({ key: '', label: '', type: 'string', required: false });
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setEditingDocType(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingDocType ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Document Type
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{docTypeToDelete?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-muted/50 border rounded-lg p-3 text-sm">
            <p className="text-muted-foreground">
              <strong>Note:</strong> If documents have been uploaded for this type, deletion will fail.
              Consider deactivating instead to keep historical data.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (docTypeToDelete) {
                  toggleActiveMutation.mutate({ id: docTypeToDelete.id, isActive: false });
                  setDeleteDialogOpen(false);
                  setDocTypeToDelete(null);
                }
              }}
              disabled={toggleActiveMutation.isPending || !docTypeToDelete?.isActive}
            >
              {toggleActiveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deactivate Instead
            </Button>
            <Button
              variant="destructive"
              onClick={() => docTypeToDelete && deleteMutation.mutate(docTypeToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from System Dialog */}
      <Dialog
        open={importDialogOpen}
        onOpenChange={(open) => {
          setImportDialogOpen(open);
          if (!open) setImportSearchTerm('');
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Import from System Document Types
            </DialogTitle>
            <DialogDescription>
              Select document types from the system library to add to your organization.
              Document types you've already imported are marked with a checkmark.
            </DialogDescription>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search document types..."
              value={importSearchTerm}
              onChange={(e) => setImportSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Document Types List */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {isLoadingSystemTypes ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : filteredSystemDocTypes.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  {importSearchTerm
                    ? 'No document types match your search'
                    : 'No system document types available'}
                </p>
              </div>
            ) : (
              (Object.keys(CATEGORY_LABELS) as DocumentCategory[]).map((category) => {
                const docTypes = groupedSystemDocTypes[category];
                if (!docTypes || docTypes.length === 0) return null;

                const CategoryIcon = getCategoryIcon(category);

                return (
                  <div key={category} className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground sticky top-0 bg-background py-1">
                      <CategoryIcon className="h-4 w-4" />
                      {CATEGORY_LABELS[category]}
                      <Badge variant="secondary" className="text-xs">{docTypes.length}</Badge>
                    </div>
                    <div className="space-y-2">
                      {docTypes.map((docType: any) => {
                        const alreadyImported = isAlreadyImported(docType.id);
                        return (
                          <div
                            key={docType.id}
                            className={cn(
                              'flex items-center gap-3 p-3 border rounded-lg transition-colors',
                              alreadyImported
                                ? 'bg-muted/50 opacity-75'
                                : 'hover:bg-muted/50'
                            )}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{docType.name}</span>
                                {docType.isRequired && (
                                  <Badge variant="destructive" className="text-xs">Required</Badge>
                                )}
                                {alreadyImported && (
                                  <Badge variant="secondary" className="text-xs">
                                    <Check className="h-3 w-3 mr-1" />
                                    Imported
                                  </Badge>
                                )}
                              </div>
                              {docType.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                  {docType.description}
                                </p>
                              )}
                              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                {docType.extractionFields?.length > 0 && (
                                  <span>{docType.extractionFields.length} extraction fields</span>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant={alreadyImported ? 'ghost' : 'default'}
                              disabled={alreadyImported || importMutation.isPending}
                              onClick={() => importMutation.mutate(docType.id)}
                            >
                              {importMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : alreadyImported ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <>
                                  <Download className="h-4 w-4 mr-1" />
                                  Import
                                </>
                              )}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
